import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { TimeField } from '@mui/x-date-pickers';
import { Controller, Control, useWatch, useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SchedulingFormData } from '@/features/scheduling/types/SchedulingFormType.interface';

type NotificationFieldRowProps = {
  control: Control<SchedulingFormData>;
  index: number;
  indexOfDay: number;
  dayDisplayName: string;
  initialShowEndTime?: boolean;
  onRemove: () => void;
};

export const NotificationFieldRow = ({
  control,
  index,
  indexOfDay,
  dayDisplayName,
  onRemove,
}: NotificationFieldRowProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<SchedulingFormData>();

  const startTime = useWatch({
    control,
    name: `schedulings.${index}.startTime`,
  });
  const endTime = useWatch({
    control,
    name: `schedulings.${index}.endTime`,
  });
  const [showEndTime, setShowEndTime] = useState<boolean>(
    startTime !== null && endTime !== null && startTime.diff(endTime) !== 0,
  );

  const toggleEndTime = () => {
    setShowEndTime((prev) => {
      const isHiding = prev === true;

      if (isHiding) {
        setValue(`schedulings.${index}.endTime`, null);
      }

      return !prev;
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 2,
        pt: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ minWidth: '200px', fontWeight: 'medium' }}
      >
        {t('pages.studies.emaQuestionnaire.scheduling.notificationLabel', {
          day: dayDisplayName,
          index: indexOfDay + 1,
        })}
      </Typography>
      <FormControlLabel
        control={
          <Switch checked={showEndTime} onChange={toggleEndTime} size="small" />
        }
        label={t('pages.questionnaireScheduling.edit.rangeTime')}
        sx={{ ml: 1 }}
      />
      <Controller
        name={`schedulings.${index}.startTime`}
        control={control}
        render={({ field: timeField }) => (
          <TimeField
            {...timeField}
            lang="fr"
            size="small"
            label={t('pages.studies.emaQuestionnaire.scheduling.startTime')}
            required
            format="HH:mm"
            ampm={false}
            sx={{ minWidth: 120, minHeight: '2rem' }}
          />
        )}
      />
      {showEndTime && (
        <Controller
          name={`schedulings.${index}.endTime`}
          control={control}
          render={({ field: timeField }) => (
            <TimeField
              {...timeField}
              required={showEndTime}
              lang="fr"
              size="small"
              label={t('pages.studies.emaQuestionnaire.scheduling.endTime')}
              format="HH:mm"
              ampm={false}
              sx={{ minWidth: 120 }}
            />
          )}
        />
      )}
      <IconButton
        onClick={onRemove}
        size="small"
        color="error"
        aria-label={t('buttons.delete')}
        sx={{ ml: 1 }}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};
